import { useState, useRef, useEffect } from 'react'

// A compact ⋮ (kebab) button that opens a small popover of per-player row
// actions on the in-round scoring card (D-346). Modeled on FriendRowMenu but
// kept SEPARATE (that one is shared with the friends UI) so the scoring menu
// can evolve independently — here it carries an optional disabled+hint action
// (Replace/Swap is guest-only and shows a disabled hint on authenticated seats).
//
// Props:
//   label    — the seat's player name, folded into the trigger's aria-label
//   actions  — [{ label, onSelect, danger?, disabled?, hint? }]
//              onSelect fires the caller's handler UNCHANGED (any confirm /
//              prompt / toast stays in the caller). A disabled action renders
//              greyed and non-interactive; `hint` is shown as its title.
export default function PlayerRowMenu({ label, actions = [] }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  // Dismiss on outside tap/click and on Escape while the menu is open.
  useEffect(() => {
    if (!open) return
    const onPointer = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('touchstart', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('touchstart', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!actions.length) return null

  return (
    <div ref={wrapRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label ? `More actions for ${label}` : 'More actions'}
        onClick={() => setOpen(o => !o)}
        style={{
          width: 30, height: 30, lineHeight: 1, padding: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'none', color: 'var(--gray-400)',
          border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)',
          cursor: 'pointer', fontSize: '1.1rem', fontWeight: 700,
        }}
      >
        ⋮
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 20,
            minWidth: 190, padding: 4,
            background: 'var(--green-dark)',
            border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)',
            boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
            display: 'flex', flexDirection: 'column',
          }}
        >
          {actions.map(a => (
            <button
              key={a.label}
              type="button"
              role="menuitem"
              disabled={!!a.disabled}
              title={a.disabled ? (a.hint || '') : ''}
              onClick={() => { if (a.disabled) return; setOpen(false); a.onSelect() }}
              style={{
                width: '100%', textAlign: 'left',
                padding: '8px 10px', background: 'none',
                color: a.disabled ? 'var(--gray-500)' : (a.danger ? 'var(--gold)' : 'var(--cream)'),
                border: 'none', borderRadius: 'calc(var(--radius) - 2px)',
                cursor: a.disabled ? 'not-allowed' : 'pointer',
                opacity: a.disabled ? 0.55 : 1,
                fontSize: '0.82rem', fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              {a.label}
              {a.disabled && a.hint && (
                <span style={{ display: 'block', fontSize: '0.66rem', fontWeight: 400, color: 'var(--gray-500)', whiteSpace: 'normal' }}>
                  {a.hint}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
